import React from 'react';
import { Box, Container, Grid, Stack, Typography, Button, Paper, Chip, Divider } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import HeadsetMicOutlinedIcon from '@mui/icons-material/HeadsetMicOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import ChatRoundedIcon from '@mui/icons-material/ChatRounded';
import PhoneInTalkRoundedIcon from '@mui/icons-material/PhoneInTalkRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import RouteRoundedIcon from '@mui/icons-material/RouteRounded';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import SmsRoundedIcon from '@mui/icons-material/SmsRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import LanguageRoundedIcon from '@mui/icons-material/LanguageRounded';
import SectionHeading from '../components/SectionHeading';
import FeatureCard from '../components/FeatureCard';
import CTASection from '../components/CTASection';
import heroTeamImage from '../assets/hero_team.png';
import conciergeAiImage from '../assets/concierge_ai.png';
import dashboardImage from '../assets/dashboard.png';
import { brand } from '../theme';

/** Matches `Container maxWidth="lg"` so hero / dashboard imagery stops at the same edge as page content. */
const LAYOUT_MAX_LG = 1200;

/**
 * Hero team photo mask: left stays soft for text; `clamp()` caps stop positions so on
 * very wide viewports the ramp does not stretch into an invisible “slow” fade — the edge
 * stays visibly softened instead of a hard bitmap-vs-navy line.
 */
const CONCIERGE_HERO_PHOTO_MASK =
  'linear-gradient(to right,' +
  ' rgba(0,0,0,0) 0px,' +
  ' rgba(0,0,0,0.08) clamp(24px, 4.5vw, 96px),' +
  ' rgba(0,0,0,0.22) clamp(56px, 11vw, 200px),' +
  ' rgba(0,0,0,0.48) clamp(96px, 21vw, 380px),' +
  ' rgba(0,0,0,0.76) clamp(140px, 31vw, 560px),' +
  ' rgba(0,0,0,0.94) clamp(180px, 40vw, 720px),' +
  ' #000 clamp(220px, 48vw, 860px),' +
  ' #000 100%)';

const FEATURES = [
  {
    icon: SupportAgentRoundedIcon,
    title: '24/7 AI Receptionist',
    description:
      'Answers every call and message with your business context, your hours, and your service details — escalates to a human only when it should.',
  },
  {
    icon: AutoAwesomeRoundedIcon,
    title: 'AI at the foundation',
    description:
      'Artimus (Art), agents, and the AI Handbook are first-class — not add-ons. The same operational intelligence powers The Hub, service catalog, quotes, work orders, routes, and reporting.',
    accent: 'gold',
  },
  {
    icon: ChatRoundedIcon,
    title: 'Unified Conversation Hub',
    description:
      'Voice, SMS, email, web leads, team, and lead views in The Hub — one inbox with realtime push, full history, and AI-suggested replies.',
  },
  {
    icon: EventAvailableRoundedIcon,
    title: 'Smart Booking',
    description:
      'Checks availability against your calendar, suggests times, books the appointment, and sends reminders automatically.',
  },
  {
    icon: ReceiptLongRoundedIcon,
    title: 'Quote-to-Cash',
    description:
      'Quick quotes, full quotes, invoices, programs, bundles, and Stripe-powered subscriptions — flowing into work orders and the field.',
    accent: 'gold',
  },
  {
    icon: RouteRoundedIcon,
    title: 'Routing & Field Ops',
    description:
      'Scheduled services, auto-generated routes, work orders, tech-side mobile views, and visit tracking — the field knows where to go and what to do.',
  },
  {
    icon: CampaignRoundedIcon,
    title: 'Marketing & Ground Game',
    description:
      'Lead pipeline, Ground Game canvassing, marketing initiatives, discount codes, and follow-up campaigns across email, SMS, and calls — measured against revenue.',
  },
  {
    icon: InsightsRoundedIcon,
    title: 'Reports That Matter',
    description:
      'Revenue, aging, manager tasks, payroll, service revenue, and telemetry — the numbers your operators run the business on.',
  },
  {
    icon: SchoolRoundedIcon,
    title: 'AI Knowledge & University',
    description:
      'Train the AI on your scripts, FAQs, and SOPs in the Handbook. New hires onboard against the same source of truth your production agents use.',
    accent: 'gold',
  },
  {
    icon: AdminPanelSettingsRoundedIcon,
    title: 'Multi-tenant & Role-aware',
    description:
      'Run multiple brands or franchises from one platform with per-business AI context, catalog, and per-user permissions.',
  },
];

const STEPS = [
  {
    n: '01',
    title: 'Connect your channels',
    description:
      'Plug in your phone (Twilio / OpenPhone / Amazon Connect), web forms, calendar, Stripe, and your Artimus (Art) context — in minutes.',
  },
  {
    n: '02',
    title: 'AI handles intake & booking',
    description:
      'Concierge answers, qualifies, quotes, books, and sends reminders — using your scripts and how you quote.',
  },
  {
    n: '03',
    title: 'You get the clean handoff',
    description:
      'Booked appointments, paid invoices, and a clear conversation history land in the dashboard your team already uses.',
  },
];

const CHANNELS = [
  { icon: PhoneInTalkRoundedIcon, label: 'Voice' },
  { icon: SmsRoundedIcon, label: 'SMS' },
  { icon: LanguageRoundedIcon, label: 'Web forms' },
  { icon: EmailRoundedIcon, label: 'Email' },
];

/** Illustrative examples only — Concierge is configured per business, not limited to these verticals. */
const INDUSTRY_EXAMPLES = [
  'Home services & trades',
  'Healthcare & dental',
  'Professional services',
  'Salons, spas & wellness',
  'Retail & local brands',
  'Field & route-based ops',
];

export default function Concierge() {
  return (
    <Box>
      <Box
        sx={{
          position: 'relative',
          color: '#fff',
          width: '100%',
          overflow: 'hidden',
          bgcolor: '#0a0f1c',
          backgroundColor: '#0a0f1c',
        }}
      >
        <Box
          sx={{
            maxWidth: LAYOUT_MAX_LG,
            mx: 'auto',
            width: '100%',
            position: 'relative',
            minHeight: { xs: 'auto', md: 520 },
          }}
        >
        <Box
          aria-hidden
          sx={{
            display: { xs: 'none', md: 'block' },
            position: 'absolute',
            inset: 0,
            zIndex: 0,
            backgroundImage: `url(${heroTeamImage})`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'auto 100%',
            backgroundPosition: 'right top',
            WebkitMaskImage: CONCIERGE_HERO_PHOTO_MASK,
            WebkitMaskSize: '100% 100%',
            WebkitMaskRepeat: 'no-repeat',
            maskImage: CONCIERGE_HERO_PHOTO_MASK,
            maskSize: '100% 100%',
            maskRepeat: 'no-repeat',
          }}
        />
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            width: '100%',
            alignItems: 'stretch',
            position: 'relative',
            zIndex: 1,
            px: { xs: 2, sm: 3 },
          }}
        >
          <Box
            sx={{
              minWidth: 0,
              boxSizing: 'border-box',
              pt: { xs: 0, md: 10 },
              pb: { xs: 22, md: 18 },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              position: 'relative',
              zIndex: 1,
              background: {
                xs: 'none',
                md: 'linear-gradient(to right, rgba(15,23,42,0.75) 0%, rgba(15,23,42,0.28) 36%, rgba(15,23,42,0.06) 62%, transparent 82%)',
              },
            }}
          >
          <Box
            component="img"
            src={conciergeAiImage}
            alt="Concierge AI"
            sx={{
              display: { xs: 'block', md: 'none' },
              mx: 'auto',
              width: '100%',
              maxWidth: { xs: 'min(100%, 440px)', sm: 'min(100%, 540px)' },
              height: 'auto',
              mb: 3,
            }}
          />
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '2.4rem', sm: '3rem', md: '3.6rem', lg: '4rem' },
              color: '#fff',
              fontWeight: 800,
              mb: 2.5,
              lineHeight: 1.05,
              textAlign: { xs: 'center', md: 'left' },
            }}
          >
            Your AI Team for{' '}
            <Box
              component="span"
              sx={{
                display: 'inline',
                WebkitBoxDecorationBreak: 'clone',
                boxDecorationBreak: 'clone',
                background: 'linear-gradient(to top, #2563eb 0%, #38bdf8 45%, #ffffff 100%)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                color: 'transparent',
              }}
            >
              Stronger Customer Relationships
            </Box>
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: '#CBD5E1',
              fontSize: { xs: '1.05rem', md: '1.15rem' },
              mb: 4,
              maxWidth: { md: 520 },
              mx: { xs: 'auto', md: 0 },
              lineHeight: 1.65,
              textAlign: { xs: 'center', md: 'left' },
            }}
          >
            Concierge is an AI-powered CRM that brings a full team of specialized AI agents
            together to automate outreach, uncover insights, and build lasting customer loyalty.
          </Typography>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent={{ xs: 'center', md: 'flex-start' }}
            alignItems={{ xs: 'stretch', sm: 'center' }}
          >
            <Button
              component={RouterLink}
              to="/contact"
              variant="contained"
              color="primary"
              size="large"
              endIcon={<ArrowForwardRoundedIcon />}
            >
              Book a demo
            </Button>
            <Button
              href="#features"
              variant="outlined"
              size="large"
              sx={{
                color: '#fff',
                borderColor: 'rgba(255,255,255,0.45)',
                '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.08)' },
              }}
            >
              See features
            </Button>
          </Stack>
        </Box>
          <Box
            aria-hidden
            sx={{
              display: { xs: 'none', md: 'block' },
              minWidth: 0,
              minHeight: { md: 1 },
            }}
          />
        </Box>
        </Box>
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 2,
            pt: 2,
            pb: { xs: 4, md: 5 },
            pointerEvents: 'none',
          }}
        >
          <Box
            sx={{
              maxWidth: LAYOUT_MAX_LG,
              mx: 'auto',
              width: '100%',
              px: { xs: 2, sm: 3 },
            }}
          >
          <Stack
            direction="row"
            useFlexGap
            spacing={{ xs: 2, md: 6 }}
            justifyContent={{ xs: 'space-evenly', md: 'flex-start' }}
            alignItems="center"
            sx={{ width: '100%', flexWrap: { xs: 'nowrap', md: 'nowrap' } }}
          >
            {[
              {
                icon: HeadsetMicOutlinedIcon,
                value: '24/7',
                label: 'agent conversations',
              },
              {
                icon: BoltOutlinedIcon,
                value: '8x',
                label: 'faster follow-ups',
              },
              {
                icon: CheckBoxOutlinedIcon,
                value: '92%',
                label: 'workflow automation',
              },
            ].map((row) => {
              const StatIcon = row.icon;
              return (
                <Box
                  key={row.label}
                  sx={{
                    flex: { xs: 1, md: '0 1 auto' },
                    minWidth: 0,
                    maxWidth: { md: 280 },
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    justifyContent="flex-start"
                  >
                    <Box
                      sx={{
                        width: { xs: 44, md: 48 },
                        height: { xs: 44, md: 48 },
                        borderRadius: '50%',
                        border: '2px solid',
                        borderColor: 'primary.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <StatIcon
                        sx={{
                          fontSize: { xs: 22, md: 24 },
                          color: 'primary.main',
                        }}
                      />
                    </Box>
                    <Box sx={{ textAlign: 'left', minWidth: 0 }}>
                      <Typography
                        component="div"
                        variant="h4"
                        sx={{
                          fontWeight: 800,
                          lineHeight: 1.1,
                          color: '#fff',
                          fontSize: { xs: '1.5rem', sm: '1.65rem', md: '2rem' },
                          textShadow:
                            '0 0 12px rgba(0,0,0,0.5), 0 2px 8px rgba(0,0,0,0.55)',
                        }}
                      >
                        {row.value}
                      </Typography>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{
                          color: '#E2E8F0',
                          fontWeight: 500,
                          mt: 0.25,
                          textShadow: '0 1px 6px rgba(0,0,0,0.55)',
                        }}
                      >
                        {row.label}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
          </Box>
        </Box>
      </Box>

      <Box id="features" sx={{ py: { xs: 10, md: 14 } }}>
        <Container maxWidth="lg">
          <SectionHeading
            eyebrow="What Concierge does"
            title="Every part of the customer journey, on autopilot."
            subtitle="Intelligence is the spine: the same AI stack powers conversations, billing, field work, and catalog data. Each tile below maps to areas teams use in the live product."
          />
          <Grid container spacing={3}>
            {FEATURES.map((f) => (
              <Grid key={f.title} size={{ xs: 12, sm: 6, md: 4 }}>
                <FeatureCard {...f} variant="dark" />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box sx={{ position: 'relative', width: '100%', overflow: 'hidden', bgcolor: '#FFFFFF' }}>
        <Box
          sx={{
            maxWidth: LAYOUT_MAX_LG,
            mx: 'auto',
            width: '100%',
            position: 'relative',
          }}
        >
          <Box
            aria-hidden
            sx={{
              display: { xs: 'none', md: 'block' },
              position: 'absolute',
              inset: 0,
              zIndex: 0,
              backgroundImage: `url(${dashboardImage})`,
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'auto 100%',
              backgroundPosition: 'right top',
            }}
          />
          <Box sx={{ position: 'relative', zIndex: 1, px: { xs: 2, sm: 3 } }}>
            <Grid
              container
              columnSpacing={{ xs: 0, md: 8 }}
              rowSpacing={{ xs: 4, md: 0 }}
              alignItems="stretch"
            >
              <Grid
                size={{ xs: 12, md: 6 }}
                sx={{
                  py: { xs: 10, md: 14 },
                  pr: { md: 1 },
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  variant="overline"
                  sx={{
                    color: brand.blue,
                    fontWeight: 700,
                    letterSpacing: '0.18em',
                    display: 'block',
                    mb: 1.5,
                  }}
                >
                  built for modern teams
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    color: brand.navy,
                    fontWeight: 800,
                    fontSize: { xs: '1.85rem', sm: '2.15rem', md: '2.5rem' },
                    lineHeight: 1.15,
                    mb: 2,
                  }}
                >
                  Ready to Build Your AI-Powered Advantage?
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: brand.slate,
                    fontSize: { xs: '1.05rem', md: '1.1rem' },
                    lineHeight: 1.65,
                    mb: 3,
                    maxWidth: 520,
                  }}
                >
                  Join forward-thinking teams that are transforming customer relationships with Concierge.
                </Typography>
                <Button
                  component={RouterLink}
                  to="/contact"
                  variant="contained"
                  color="primary"
                  size="large"
                  endIcon={<ArrowForwardRoundedIcon />}
                  sx={{ px: 3, py: 1.25, alignSelf: 'flex-start' }}
                >
                  Schedule a Demo
                </Button>
              </Grid>
              <Grid
                size={{ xs: 12, md: 6 }}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignSelf: 'stretch',
                  minHeight: { md: 1 },
                }}
              >
                <Box
                  component="img"
                  src={dashboardImage}
                  alt="Concierge dashboard preview"
                  sx={{
                    display: { xs: 'block', md: 'none' },
                    width: '100%',
                    height: 'auto',
                    borderRadius: 2,
                    border: `1px solid ${brand.border}`,
                    boxShadow: '0 16px 48px -20px rgba(15,23,42,0.22)',
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Box>

      <Box sx={{ py: { xs: 10, md: 14 }, bgcolor: '#FFFFFF', borderTop: `1px solid ${brand.border}`, borderBottom: `1px solid ${brand.border}` }}>
        <Container maxWidth="lg">
          <SectionHeading
            eyebrow="How it works"
            title="From first ring to paid invoice — without your team lifting a finger."
          />
          <Grid container spacing={4}>
            {STEPS.map((s) => (
              <Grid key={s.n} size={{ xs: 12, md: 4 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 3, md: 4 },
                    height: '100%',
                    borderRadius: 3,
                    position: 'relative',
                    overflow: 'hidden',
                    bgcolor: brand.navy,
                    color: '#fff',
                    border: '1px solid rgba(255,255,255,0.12)',
                  }}
                >
                  <Box
                    aria-hidden
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      background: brand.ctaPanelGradient,
                      pointerEvents: 'none',
                    }}
                  />
                  <Box sx={{ position: 'relative', zIndex: 1 }}>
                  <Typography
                    variant="h2"
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 16,
                      fontSize: '4.5rem',
                      color: 'rgba(255,255,255,0.07)',
                      fontWeight: 800,
                      lineHeight: 1,
                    }}
                  >
                    {s.n}
                  </Typography>
                  <Typography variant="overline" sx={{ color: brand.amberAccent, fontWeight: 700, letterSpacing: '0.14em' }}>
                    Step {s.n}
                  </Typography>
                  <Typography variant="h5" sx={{ mt: 1, mb: 1.5, fontWeight: 700, color: '#fff' }}>
                    {s.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#fff' }}>
                    {s.description}
                  </Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ my: { xs: 6, md: 8 } }} />

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={4}
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h6" sx={{ color: brand.slate, letterSpacing: '0.14em', fontSize: '0.85rem' }}>
              Channels covered out of the box
            </Typography>
            <Stack direction="row" spacing={{ xs: 3, md: 5 }} flexWrap="wrap" useFlexGap>
              {CHANNELS.map((c) => (
                <Stack key={c.label} direction="row" spacing={1.25} alignItems="center">
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 1.5,
                      bgcolor: 'rgba(30,91,255,0.10)',
                      color: brand.blue,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <c.icon fontSize="small" />
                  </Box>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: brand.navy }}>
                    {c.label}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Stack>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 10, md: 14 } }}>
        <Container maxWidth="lg">
          <SectionHeading
            eyebrow="Where it fits"
            title="Built for any industry"
            subtitle="Concierge is not locked to a fixed list of verticals—we configure AI context, scripts, catalog, and workflows for how you operate. The tags below are a sample of sectors teams use in production today, not the full picture."
          />
          <Stack
            direction="row"
            flexWrap="wrap"
            justifyContent="center"
            useFlexGap
            spacing={1.5}
            sx={{ rowGap: 2 }}
          >
            {INDUSTRY_EXAMPLES.map((i) => (
              <Chip
                key={i}
                label={i}
                sx={{
                  bgcolor: '#fff',
                  border: `1px solid ${brand.border}`,
                  color: brand.navy,
                  fontWeight: 600,
                  px: 1.5,
                  py: 2.25,
                  fontSize: '0.95rem',
                }}
              />
            ))}
          </Stack>
        </Container>
      </Box>

      <CTASection
        eyebrow="See it on your business"
        title="20 minutes. Your numbers. A real demo."
        subtitle="Bring a sample of how leads come in today. We’ll show you exactly how Concierge handles them — voice, SMS, calendar, and all."
      />
    </Box>
  );
}
